import React, { useState } from 'react';
import { Music, Sparkles, Heart, Loader2, Play, AlertCircle } from 'lucide-react';

export default function VibeCheck() {
  const [mood, setMood] = useState('');
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const moodExamples = [
    'Late night drive in the rain',
    'High-energy gym session',
    'Relaxing Sunday morning',
    'Focused coding session',
    'Romantic dinner date',
    'Post-breakup sad songs'
  ];

  const handleGenerate = async () => {
    if (!mood.trim()) return;

    setLoading(true);
    setError('');
    setPlaylist(null);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const response = await fetch(`${API_URL}/api/generate-playlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mood })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to generate playlist');
      }

      setPlaylist(data);
    } catch (err) {
      setError(err.message || 'Unable to generate playlist. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <header className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Music className="w-12 h-12 text-purple-300" />
            <h1 className="text-5xl font-bold bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">
              VibeCheck
            </h1>
          </div>
          <p className="text-xl text-purple-200 mb-2">
            Discover the perfect YouTube playlist for your mood
          </p>
          
          {/* Spotify Coming Soon Notice */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 border border-yellow-500/30 rounded-full text-yellow-200 text-sm mt-4">
            <AlertCircle className="w-4 h-4" />
            <span>Spotify integration coming soon!</span>
          </div>
        </header>

        {/* Mood Input */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 mb-8 border border-white/20">
          <div className="space-y-6">
            <div>
              <label className="block text-lg font-medium mb-3 text-purple-200">
                How are you feeling?
              </label>
              <textarea
                value={mood}
                onChange={(e) => setMood(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Describe your current mood or situation..."
                className="w-full px-4 py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-purple-200 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                rows={3}
                maxLength={200}
              />
              <p className="text-sm text-purple-300 mt-2">
                {mood.length}/200 characters
              </p>
            </div>

            {/* Example moods */}
            <div>
              <p className="text-sm text-purple-300 mb-2">Try these:</p>
              <div className="flex flex-wrap gap-2">
                {moodExamples.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setMood(example)}
                    className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded-full text-sm transition-colors border border-white/20"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>

            {/* YouTube Badge */}
            <div className="flex items-center justify-center gap-2 py-3 px-6 bg-red-500/20 rounded-xl border border-red-500/30">
              <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              <span className="font-medium text-red-200">Powered by YouTube Music</span>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={loading || !mood.trim()}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl font-bold text-lg hover:shadow-lg hover:shadow-purple-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating your playlist...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Generate Playlist
                </>
              )}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}
        </div>

        {/* Playlist Results */}
        {playlist && (
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-3xl font-bold mb-2">Your Playlist</h2>
                <p className="text-purple-200">
                  Based on: <span className="italic">"{playlist.mood}"</span>
                </p>
              </div>
              <span className="px-4 py-2 bg-purple-500/30 rounded-full text-sm font-medium">
                {playlist.count} videos
              </span>
            </div>

            {/* Featured Video Player */}
            {playlist.tracks.length > 0 && (
              <div className="aspect-video w-full rounded-xl overflow-hidden shadow-2xl">
                <iframe
                  src={`https://www.youtube.com/embed/${playlist.tracks[0].id}?autoplay=0&rel=0`}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="YouTube Player"
                  className="w-full h-full"
                />
              </div>
            )}

            {/* Track List */}
            <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
              {playlist.tracks.map((track, idx) => (
                <div
                  key={track.id}
                  className="group flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all cursor-pointer"
                  onClick={() => window.open(`https://www.youtube.com/watch?v=${track.id}`, '_blank')}
                >
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-500/30 rounded-full flex items-center justify-center text-sm font-bold">
                    {idx + 1}
                  </div>
                  
                  <div className="relative flex-shrink-0">
                    <img
                      src={track.thumbnail}
                      alt={track.title}
                      className="w-20 h-14 rounded object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded">
                      <Play className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium truncate group-hover:text-purple-300 transition-colors">
                      {track.title}
                    </h3>
                    <p className="text-sm text-purple-300 truncate">
                      {track.channel}
                    </p>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`https://www.youtube.com/watch?v=${track.id}`, '_blank');
                    }}
                    className="flex-shrink-0 p-2 bg-red-500/20 hover:bg-red-500/40 rounded-full transition-colors"
                  >
                    <Play className="w-5 h-5 text-red-300" />
                  </button>
                </div>
              ))}
            </div>

            {/* Playlist Actions */}
            <div className="flex gap-3 pt-4 border-t border-white/10">
              <button
                onClick={() => {
                  const videoIds = playlist.tracks.map(t => t.id).join(',');
                  window.open(`https://www.youtube.com/watch_videos?video_ids=${videoIds}`, '_blank');
                }}
                className="flex-1 py-3 px-4 bg-red-500/20 hover:bg-red-500/30 rounded-xl font-medium transition-colors border border-red-500/30"
              >
                Watch All on YouTube
              </button>
              <button
                onClick={handleGenerate}
                className="flex-1 py-3 px-4 bg-purple-500/20 hover:bg-purple-500/30 rounded-xl font-medium transition-colors border border-purple-500/30"
              >
                Generate New Playlist
              </button>
            </div>
          </div>
        )}

        {/* Support Section */}
        <div className="mt-12 text-center">
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
            <Heart className="w-12 h-12 text-pink-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-3">Love VibeCheck?</h3>
            <p className="text-purple-200 mb-6 max-w-md mx-auto">
              Support us with a coffee to keep the vibes flowing and help us add Spotify integration!
            </p>
            
            <a
              href="https://www.buymeacoffee.com/vibecheck.bar"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold rounded-xl transition-all hover:shadow-lg hover:shadow-yellow-500/50"
            >
              <span className="text-2xl">☕</span>
              Buy Me a Coffee
            </a>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center text-purple-300 text-sm space-y-2">
          <p>Powered by YouTube Music • Made with ♥ for music lovers</p>
          <p className="text-purple-400 text-xs">
            Spotify integration temporarily unavailable while they update their platform
          </p>
        </footer>
      </div>
    </div>
  );
}